import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  bindingIndex,
  isModifierOnly,
  normalizeCombo,
  PDV_SHORTCUT_META,
  resolveBindings,
  splitQtyPrefix,
  type PdvShortcutAction,
  type PdvShortcutConfig,
} from "@vertex/validators/pdv-pure";

/**
 * Registry de atalhos: **um único listener** em `window` com `capture: true`.
 *
 * Um listener por componente pareceria mais simples e seria errado: a ordem de
 * disparo passaria a depender da ordem de montagem, dois handlers responderiam
 * ao mesmo F3 e não haveria como um diálogo "engolir" o Escape do que está
 * atrás. Com um listener só, a precedência é explícita e auditável.
 */

/** Handler devolve `true` quando CONSUMIU a tecla (aí vem preventDefault). */
export type ShortcutHandler = () => boolean | void;

type Handlers = Partial<Record<PdvShortcutAction, ShortcutHandler>>;

interface Escopo {
  id: number;
  nome: string;
  /** Getter, não o objeto: os handlers são closures que mudam a cada render. */
  ler: () => Handlers;
}

interface RegistryApi {
  push(nome: string, ler: () => Handlers): () => void;
  /** Combos de uma ação, pra desenhar rodapé e ajuda sem duplicar a resolução. */
  combos(action: PdvShortcutAction): string[];
  /** Ações com atalho ativo no escopo do topo (é o que o F1 deve listar). */
  acoesAtivas(): PdvShortcutAction[];
}

const Ctx = createContext<RegistryApi | null>(null);

export function useShortcutRegistry(): RegistryApi {
  const api = useContext(Ctx);
  if (!api) throw new Error("useShortcutRegistry fora do ShortcutProvider");
  return api;
}

/**
 * Registra handlers enquanto o componente está montado. O escopo empilha: a
 * venda empurra o dela, o diálogo de pagamento empurra o seu por cima, e **só
 * o topo responde**. É isso que impede o F3 de abrir um segundo checkout por
 * cima do primeiro.
 */
export function useKeyScope(nome: string, handlers: Handlers, ativo = true): void {
  const api = useShortcutRegistry();
  /* Os handlers mudam a cada render (são closures sobre o estado da tela).
     Guardar numa ref e registrar um GETTER evita empilhar e desempilhar o
     escopo a cada tecla digitada — o que reordenaria a pilha no meio da venda
     e faria o Escape fechar a coisa errada. */
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!ativo) return;
    return api.push(nome, () => ref.current);
  }, [api, nome, ativo]);
}

export function ShortcutProvider({
  config,
  onScan,
  children,
}: {
  config: PdvShortcutConfig | null;
  /** Bipe detectado pelo wedge. Recebe o código lido e a quantidade prefixada. */
  onScan?: (code: string, qty: number) => void;
  children: ReactNode;
}) {
  const bindings = useMemo(() => resolveBindings(config), [config]);
  const index = useMemo(() => bindingIndex(bindings), [bindings]);
  const escopos = useRef<Escopo[]>([]);
  const seq = useRef(0);
  const scanRef = useRef(onScan);
  scanRef.current = onScan;

  const push = useCallback<RegistryApi["push"]>((nome, ler) => {
    const id = ++seq.current;
    escopos.current.push({ id, nome, ler });
    return () => {
      escopos.current = escopos.current.filter((s) => s.id !== id);
    };
  }, []);

  const api = useMemo<RegistryApi>(
    () => ({
      push,
      combos: (action) => bindings[action] ?? [],
      acoesAtivas: () => {
        const topo = escopos.current[escopos.current.length - 1];
        if (!topo) return [];
        const base = escopos.current[0];
        /* Global (o fundo da pilha) + topo: é o que o operador pode fazer
           agora, que é a única lista honesta pra ajuda. */
        const keys = new Set<PdvShortcutAction>();
        for (const s of base === topo ? [topo] : [base!, topo]) {
          for (const k of Object.keys(s.ler()) as PdvShortcutAction[]) keys.add(k);
        }
        return [...keys].filter((a) => (bindings[a] ?? []).length > 0);
      },
    }),
    [push, bindings],
  );

  const wedge = useRef<{ buffer: Array<{ char: string; t: number }> }>({ buffer: [] });

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // 1 ─ Composição (acento morto do ABNT2) e repetição de tecla segurada.
      if (e.isComposing) return;

      const combo = normalizeCombo(e);
      if (isModifierOnly(e.code)) return;

      // 2/3 ─ Resolve na pilha de escopos, do topo pro fundo.
      const action = index.get(combo);
      if (action) {
        if (e.repeat && !PDV_SHORTCUT_META[action].repeatable) return;

        const pilha = escopos.current;
        for (let i = pilha.length - 1; i >= 0; i--) {
          const handler = pilha[i]!.ler()[action];
          if (handler) {
            // 4 ─ Consumiu? Então a tecla morre aqui.
            if (handler() !== false) {
              e.preventDefault();
              e.stopPropagation();
            }
            return;
          }
          /* `back` atravessa a pilha inteira: Escape tem que fechar o que
             estiver aberto, mesmo que o escopo do topo não o declare. Qualquer
             outra ação para no topo — é o que dá previsibilidade. */
          if (action !== "back") return;
        }
        return;
      }

      // 5 ─ Só agora o wedge vê o evento. Tecla de FUNÇÃO nunca o alimenta.
      alimentaWedge(e);
    }

    /**
     * Leitor USB-HID tem assinatura mensurável: 8 a 13 caracteres com 5 a 20 ms
     * entre teclas, terminando em Enter. Humano digita a 80–300 ms por tecla.
     * É essa diferença — e não o foco de um campo — que distingue os dois.
     */
    function alimentaWedge(e: KeyboardEvent) {
      const alvo = e.target as HTMLElement | null;
      /* Campo de texto livre (motivo de sangria, nome de cliente) marca
         `data-no-scan`: ali o operador digita frase, e um "Enter" não é bipe. */
      if (alvo?.closest?.("[data-no-scan]")) return;

      const agora = e.timeStamp || Date.now();
      const buf = wedge.current.buffer;
      const ultimo = buf[buf.length - 1];
      /* Gap > 50 ms quebra a sequência: o que veio antes era digitação. */
      if (ultimo && agora - ultimo.t > 50) buf.length = 0;

      if (e.key.length === 1) {
        buf.push({ char: e.key, t: agora });
        return;
      }

      if (e.code !== "Enter" && e.code !== "NumpadEnter") return;
      if (buf.length < 4) {
        buf.length = 0;
        return;
      }
      const gaps: number[] = [];
      for (let i = 1; i < buf.length; i++) gaps.push(buf[i]!.t - buf[i - 1]!.t);
      const ordenados = [...gaps].sort((a, b) => a - b);
      const mediana = ordenados.length
        ? ordenados[ordenados.length >> 1]!
        : Number.POSITIVE_INFINITY;

      const lido = buf.map((b) => b.char).join("");
      buf.length = 0;
      if (mediana >= 30) return; // Foi humano digitando rápido, não bipe.

      /* Bipe: entra direto no carrinho, sem passar pela busca incremental.
         Zero debounce — o operador bipa o próximo item antes do primeiro
         aparecer, e esperar 180 ms por item perderia leitura.

         O prefixo `3*789456` continua valendo: quem digita a quantidade e
         DEPOIS bipa produz uma sequência única, e perder isso quebraria o
         fluxo mais rápido do balcão. */
      const { qty, code } = splitQtyPrefix(lido);
      e.preventDefault();
      e.stopPropagation();
      scanRef.current?.(code, qty);
    }

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [index]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}
